import type { RouterConfig } from '@nuxt/schema'

// vue-router 5's `RouterOptions['parseQuery']` normalizes every query value to
// an array (e.g. `?a` becomes `{ a: [null] }`), which differs from the
// scalar-or-array `LocationQuery` type the package exports publicly. Deriving
// the type straight from `RouterConfig` keeps us aligned with whichever
// vue-router instance Nuxt actually wires up.
type RouterQuery = ReturnType<NonNullable<RouterConfig['parseQuery']>>

/*
 * Consumers read `route.query.x` as a scalar; vue-router 5's typings only
 * allow arrays, so scalars are cast rather than wrapped.
 */
const asQueryValue = (value: string | null) =>
  value as unknown as RouterQuery[string]

type ArrayWithDelimiter = string[] & {
  delimiter: string
}

/**
 * @description Represents the list of characters which can be used for delimiting multi-value query params
 */
const DELIMITER_REGEX = new RegExp(/[|,]/, 'g')

// https://router.vuejs.org/api/interfaces/routeroptions.html
const config: RouterConfig = {
  parseQuery(search): RouterQuery {
    const params = new URLSearchParams(search)
    const finalQuery: RouterQuery = {}

    for (const key of params.keys()) {
      const value = params.get(key)

      if (!value) {
        finalQuery[key] = asQueryValue(value)
        // skip processing for null values
        continue
      }

      const matched = value.match(DELIMITER_REGEX)

      if (matched?.length) {
        const [delimiter] = matched
        const values = value.split(DELIMITER_REGEX) as ArrayWithDelimiter

        Object.defineProperty(values, 'delimiter', {
          value: delimiter,
          writable: false,
          enumerable: false,
          configurable: true,
        })

        finalQuery[key] = values
      } else {
        finalQuery[key] = asQueryValue(value)
      }
    }

    return finalQuery
  },
  scrollBehavior() {
    return
  },
  stringifyQuery(query) {
    const params: Record<string, string> = {}

    for (const key in query) {
      const value = query[key]

      if (Array.isArray(value)) {
        params[key] = value.join((value as ArrayWithDelimiter).delimiter || ',')
      } else if (value !== undefined && value !== null) {
        params[key] = String(value)
      }
    }

    return new URLSearchParams(params).toString()
  },
}

export default config
